import { Component, lazy, Suspense, useEffect, useState, useCallback } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useAuth } from './auth/AuthContext';
import { useIdleLogout } from './auth/useIdleLogout';
import { setTokenGetter } from './api/client';
import LoginPage from './auth/LoginPage';
import AppLayout from './layouts/AppLayout';
import ToastProvider from './components/ToastProvider';
import DemoResetButton from './components/DemoResetButton';
import InstallCoach from './components/InstallCoach';
import ServiceWorkerUpdatePrompt from './components/ServiceWorkerUpdatePrompt';

const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === 'true';
import { useCurrentUser } from './hooks/useCurrentUser';
import { APP_REDIRECT_ROUTES } from './routes/appRedirectRoutes';
import PageLoader from './components/PageLoader';
import IntakeQueueRoute from './routes/IntakeQueueRoute';
import { useAppBadge } from './hooks/useAppBadge';
import { useSwNavigationBridge } from './hooks/useSwNavigationBridge';
import { usePushSubscription, usePushBootResubscribe } from './hooks/usePushSubscription';
import { isStandaloneDisplayMode } from './utils/displayMode';
import { recoverFromStaleAssetError } from './lib/staleAssetRecovery';

// P3-A12/PP12: the receiving half of src/sw.ts's notificationclick hand-off
// (see hooks/useSwNavigationBridge.ts) — must be mounted INSIDE
// <BrowserRouter> since it needs useNavigate()/useLocation(). A tiny wrapper
// component (rather than calling the hook directly in App(), which renders
// outside any Router until the return statement below) keeps the mount site
// self-documenting at the call site.
function SwNavigationBridge() {
  useSwNavigationBridge();
  return null;
}

// Error boundary — catches lazy-loading failures and render errors.
// Prevents blank page when service worker serves stale chunks during deploys.
// Pass a `resetKey` prop (e.g. location.pathname) so the boundary resets on navigation.
export class ChunkErrorBoundary extends Component<
  { children: ReactNode; resetKey?: string },
  { hasError: boolean; error: Error | null; componentStack: string | null }
> {
  state: { hasError: boolean; error: Error | null; componentStack: string | null } = {
    hasError: false,
    error: null,
    componentStack: null,
  };
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error, componentStack: null };
  }
  componentDidUpdate(prevProps: { resetKey?: string }) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, componentStack: null });
    }
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ChunkErrorBoundary caught:', error, info);
    this.setState({ error, componentStack: info.componentStack ?? null });
    void recoverFromStaleAssetError(
      error,
      window.sessionStorage,
      () => window.location.reload(),
    ).then((recovered) => {
      if (!recovered) {
        Sentry.captureException(error, { contexts: { react: { componentStack: info.componentStack } } });
      }
    }).catch((recoveryError) => {
      console.warn('ChunkErrorBoundary recovery failed:', recoveryError);
      Sentry.captureException(error, { contexts: { react: { componentStack: info.componentStack } } });
    });
  }
  render() {
    if (this.state.hasError) {
      const isDev = import.meta.env.DEV;
      const errorMessage = this.state.error ? this.state.error.message : 'Unknown error';
      const componentStack = this.state.componentStack;
      const looksLikeViteOptimizeFailure = isDev && (
        errorMessage.includes('Failed to fetch dynamically imported module')
        || errorMessage.includes('/node_modules/.vite/deps/')
      );
      return (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '100vh', background: 'var(--bg, #fefcf9)',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}>
          <div style={{ textAlign: 'center', maxWidth: 360, padding: '1rem' }}>
            <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text, #1a1023)', marginBottom: '0.5rem' }}>
              Something went wrong
            </div>
            <p style={{ color: 'var(--text-muted, #a8a29e)', fontSize: '0.85rem', marginBottom: '1rem' }}>
              {isDev ? 'Dev runtime error in this route. Details are shown below.' : 'A new version may have been deployed. Reload to get the latest.'}
            </p>
            {looksLikeViteOptimizeFailure && (
              <p style={{ color: 'var(--text, #1a1023)', fontSize: '0.8rem', marginBottom: '1rem' }}>
                This often means the Vite dev server is serving a stale optimized dependency. Restart `npm run dev` and reload the page.
              </p>
            )}
            {isDev && (
              <div
                style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  textAlign: 'left',
                  background: 'rgba(15, 23, 42, 0.06)',
                  border: '1px solid rgba(148, 163, 184, 0.35)',
                  borderRadius: 8,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
                  fontSize: '0.75rem',
                  color: '#1f2937',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                }}
              >
                <strong style={{ display: 'block', marginBottom: '0.4rem' }}>{errorMessage}</strong>
                {componentStack && (
                  <span>{componentStack.trim()}</span>
                )}
              </div>
            )}
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.5rem 1.25rem', borderRadius: 6,
                background: 'var(--primary, #f43f5e)', color: '#fff',
                border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500,
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Wrapper that passes location.pathname as resetKey so the boundary clears on navigation
function LocationAwareErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ChunkErrorBoundary resetKey={location.pathname}>{children}</ChunkErrorBoundary>;
}

function BillingReviewRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/', search: location.search, hash: 'moneta' }} replace />;
}

function LegacyKpiStaffRedirect({ intakeWork = false }: { intakeWork?: boolean }) {
  const { userId } = useParams<{ userId?: string }>();
  const location = useLocation();
  const pathname = userId
    ? `/admin/kpis/staff/${userId}${intakeWork ? '/intake-work' : ''}`
    : '/admin/kpis';
  return <Navigate to={{ pathname, search: location.search, hash: location.hash }} replace />;
}

// Lazy-loaded pages — each becomes its own chunk
const Dashboard = lazy(() => import('./pages/Dashboard'));
const TasksPage = lazy(() => import('./pages/tasks/TasksPage'));
const AIHubPage = lazy(() => import('./pages/AIHubPage'));
const InHubChatRoute = lazy(() => import('./pages/ai-hub/InHubChatRoute'));
const BringoPilotReviewPage = lazy(() => import('./pages/ai-hub/BringoPilotReviewPage'));
const PipelineBoard = lazy(() => import('./pages/PipelineBoard'));
const ContactsList = lazy(() => import('./pages/ContactsList'));
const ContactDetail = lazy(() => import('./pages/ContactDetail'));
const MatterDetail = lazy(() => import('./pages/MatterDetail'));
const MattersList = lazy(() => import('./pages/MattersList'));
const LeadsQueue = lazy(() => import('./pages/LeadsQueue'));
const AdminCustomFields = lazy(() => import('./pages/AdminCustomFields'));
const AdminRcSync = lazy(() => import('./pages/AdminRcSync'));
const AdminDropbox = lazy(() => import('./pages/AdminDropbox'));
const AdminEmail = lazy(() => import('./pages/AdminEmail'));
const AdminRulesEngine = lazy(() => import('./pages/AdminRulesEngine'));
const AdminTaskTemplates = lazy(() => import('./pages/AdminTaskTemplates'));
const AdminRetainers = lazy(() => import('./pages/AdminRetainers'));
const AdminOperationsCompliance = lazy(() => import('./pages/AdminOperationsCompliance'));
const AdminProtocols = lazy(() => import('./pages/AdminProtocols'));
const AdminTodoist = lazy(() => import('./pages/AdminTodoist'));
const AdminForms = lazy(() => import('./pages/AdminForms'));
const AdminSignatures = lazy(() => import('./pages/AdminSignatures'));
const AdminSignatureTemplates = lazy(() => import('./pages/AdminSignatureTemplates'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const AdminFileRouter = lazy(() => import('./pages/AdminFileRouter'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const AdminFeedback = lazy(() => import('./pages/AdminFeedback'));
const AdminPipelines = lazy(() => import('./pages/AdminPipelines'));
const AdminKpis = lazy(() => import('./pages/AdminKpis'));
const AdminPracticeAreas = lazy(() => import('./pages/AdminPracticeAreas'));
const AdminWebhooks = lazy(() => import('./pages/AdminWebhooks'));
const AdminDataImport = lazy(() => import('./pages/AdminDataImport'));
const DataImportClioCallback = lazy(() => import('./pages/AdminDataImport').then(m => ({ default: m.DataImportClioCallback })));
const AdminTags = lazy(() => import('./pages/AdminTags'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminDeparture = lazy(() => import('./pages/AdminDeparture'));
const AdminAICommandCenter = lazy(() => import('./pages/AdminAICommandCenter'));
const AdminAICostPage = lazy(() => import('./pages/ai-hub/AdminAICostPage'));
const BillingExportPage = lazy(() => import('./pages/BillingExportPage'));
const BillingDetailPage = lazy(() => import('./pages/BillingDetailPage'));
const AdminAiBillingPage = lazy(() => import('./pages/AdminAiBillingPage'));
const CallCaseBountiesPage = lazy(() => import('./pages/CallCaseBountiesPage'));
const AdminTrainingModules = lazy(() => import('./pages/AdminTrainingModules'));
const IntakeForm = lazy(() => import('./pages/IntakeForm'));
const MySettings = lazy(() => import('./pages/MySettings'));
const CourtFormatsPage = lazy(() => import('./pages/CourtFormatsPage'));
const UserGuide = lazy(() => import('./pages/UserGuide'));
const AIVerificationHelpPage = lazy(() => import('./pages/AIVerificationHelpPage'));
const StaffPortalPage = lazy(() => import('./pages/StaffPortalPage'));
const EmployeeManualPage = lazy(() => import('./pages/EmployeeManualPage'));
// The gate renders today's LawLibraryPage unless the new page is switched on for this user (#1964).
const LawLibraryGate = lazy(() => import('./pages/lawlibrary/shell/LawLibraryGate'));
// /library remains a Staff Portal redirect for the legacy SharePoint knowledge base.

const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const UnmatchedComms = lazy(() => import('./pages/UnmatchedComms'));
const CommunicationsQuarantinePage = lazy(() => import('./pages/CommunicationsQuarantinePage'));
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'));
// PortalApprovalQueue + PipelineMonitor moved to Dashboard subtabs — routes redirect to /
const PortalLayout = lazy(() => import('./portal/PortalLayout'));
const PortalLogin = lazy(() => import('./portal/PortalLogin'));
const PortalVerify = lazy(() => import('./portal/PortalVerify'));
const PortalDashboard = lazy(() => import('./portal/PortalDashboard'));
const PublicFormPage = lazy(() => import('./pages/PublicFormPage'));
const ResetPasswordPage = lazy(() => import('./auth/ResetPasswordPage'));
const VoiceModePage = lazy(() => import('./pages/VoiceModePage'));
const MaverickViewerRedirect = lazy(() => import('./pages/MaverickViewerRedirect'));
const AdminDripCampaigns = lazy(() => import('./pages/AdminDripCampaigns'));
const AttorneyQueuePage = lazy(() => import('./pages/AttorneyQueuePage'));
const PhoneRegistryAdmin = lazy(() => import('./pages/PhoneRegistryAdmin'));
const CreditorMatchReview = lazy(() => import('./pages/CreditorMatchReview'));
const AdminMailOps = lazy(() => import('./pages/AdminMailOps'));
const AdminIntakeOps = lazy(() => import('./pages/AdminIntakeOps'));
const AdminIntakeSetup = lazy(() => import('./pages/AdminIntakeSetup'));
const CommandCenter = lazy(() => import('./pages/CommandCenter'));
const KpiStaffDetailPage = lazy(() => import('./pages/KpiStaffDetailPage'));
const KpiStaffIntakeWorkPage = lazy(() => import('./pages/KpiStaffIntakeWorkPage'));
const KpiAlertsPage = lazy(() => import('./pages/KpiAlertsPage'));
const KpiGuidePage = lazy(() => import('./pages/KpiGuidePage'));
const WorkbenchPage = lazy(() => import('./pages/WorkbenchPage'));
const SchedulingOrderReviewPage = lazy(() => import('./pages/SchedulingOrderReviewPage'));
const MattersNeedingReviewPage = lazy(() => import('./pages/MattersNeedingReviewPage'));
const BulletinBoardPage = lazy(() => import('./pages/BulletinBoardPage'));
const AdminBulletinBoard = lazy(() => import('./pages/AdminBulletinBoard'));
const AdminBilling = lazy(() => import('./pages/AdminBilling'));
const DataIntegrityReview = lazy(() => import('./pages/DataIntegrityReview'));

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { data: currentUser, isLoading } = useCurrentUser();
  if (isLoading) return <PageLoader />;
  if (currentUser && currentUser.role !== 'admin') {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <h2 style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: '1.1rem' }}>Access Denied</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>You need admin permissions to access this page.</p>
      </div>
    );
  }
  return <>{children}</>;
}

function BillingClerkRoute({ children }: { children: React.ReactNode }) {
  const { data: currentUser, isLoading } = useCurrentUser();
  if (isLoading) return <PageLoader />;
  if (currentUser && currentUser.role !== 'admin' && !currentUser.is_billing_clerk) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <h2 style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: '1.1rem' }}>Access Denied</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>You need billing clerk permissions to access this page.</p>
      </div>
    );
  }
  return <>{children}</>;
}

function StaffRoute({ children }: { children: React.ReactNode }) {
  const { data: currentUser, isLoading } = useCurrentUser();
  if (isLoading) return <PageLoader />;
  if (currentUser && !['admin', 'attorney', 'paralegal', 'intake', 'intake_supervisor'].includes(currentUser.role)) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <h2 style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: '1.1rem' }}>Access Denied</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>You need staff permissions to access this page.</p>
      </div>
    );
  }
  return <>{children}</>;
}

export function AttorneyAdminRoute({ children }: { children: React.ReactNode }) {
  const { data: currentUser, isLoading } = useCurrentUser();
  if (isLoading) return <PageLoader />;
  if (!currentUser || !['admin', 'attorney'].includes(currentUser.role)) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <h2 style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: '1.1rem' }}>Access Denied</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>You need attorney or admin permissions to access this page.</p>
      </div>
    );
  }
  return <>{children}</>;
}

function KpiSupervisorRoute({ children }: { children: React.ReactNode }) {
  const { data: currentUser, isLoading } = useCurrentUser();
  if (isLoading) return <PageLoader />;
  if (currentUser && !['admin', 'intake_supervisor'].includes(currentUser.role)) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <h2 style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: '1.1rem' }}>Access Denied</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>You need KPI supervisor permissions to access this page.</p>
      </div>
    );
  }
  return <>{children}</>;
}

export default function App() {
  const { isAuthenticated, isLoading, getAccessToken, login, logout, logoutIssue, authEnabled, authMethods, markNativeUnauthenticated, activeAuthMethod, nativeRemember, idleMinutes } = useAuth();

  // P3-A9: the icon badge's single mount point. Internally no-ops via its own
  // `if (!user) return` guard whenever useCurrentUser hasn't resolved yet or
  // 401s, so mounting it unconditionally here — before the early-return
  // branches below for form/portal/reset routes — is safe: those routes
  // never authenticate as a staff user, so `user` stays undefined there and
  // the hook's effect skips everything.
  useAppBadge();

  // P3-A12/Task 8: the boot-time push-resubscribe/rotation path (PP15) —
  // internally self-guards on user/standalone/permission/buzz-toggle
  // exactly like useAppBadge above, so it's safe to mount unconditionally.
  usePushBootResubscribe();
  const { subscribe: subscribeToPush } = usePushSubscription();

  // Idle logout: fire after `idleMinutes` of no real user activity.
  // Only active for default-mode (non-remember) native sessions, AND never
  // while running as an installed/standalone PWA (P3-A7) — a staffer who put
  // Valentine on their home screen shouldn't get logged out from idling on
  // their own phone the way an unattended browser tab in the office should.
  // Placed before any early returns to satisfy rules-of-hooks; `enabled` gates the timer.
  const handleIdle = useCallback(() => {
    void logout();
  }, [logout]);
  useIdleLogout(
    idleMinutes,
    isAuthenticated && activeAuthMethod === 'native' && !nativeRemember && !isStandaloneDisplayMode(),
    handleIdle,
  );

  // Wire token getter to API client
  useEffect(() => {
    if (authEnabled) {
      setTokenGetter(getAccessToken);
    }
  }, [authEnabled, getAccessToken]);

  // Set Sentry user context when current user is available
  const { data: sentryUser } = useCurrentUser();
  useEffect(() => {
    if (sentryUser) {
      Sentry.setUser({ id: String(sentryUser.id), email: sentryUser.email, username: sentryUser.name });
    }
  }, [sentryUser]);

  // Handle 401 responses — re-authenticate instead of signing out.
  // Previously called logout() which did logoutRedirect() — this signed the user completely
  // out of Azure AD, showing "you've been signed out" and requiring fresh credentials.
  // Now calls login() which does loginRedirect() — if their Azure AD session is still
  // valid (SSO), they're seamlessly re-authenticated. If not, they see a login page.
  // The auth:unauthorized event is already debounced (100ms) to prevent duplicate redirects.
  useEffect(() => {
    if (!authEnabled) return;
    const handler = () => {
      if (authMethods.includes('native')) {
        // native (or both): drop to the login page; do NOT MSAL-redirect
        markNativeUnauthenticated();
      } else {
        // Microsoft-only tenant: re-auth via Microsoft (preserves prior behavior)
        console.warn('Received 401 — redirecting to re-authenticate (not signing out)');
        login();
      }
    };
    window.addEventListener('auth:unauthorized', handler);
    return () => window.removeEventListener('auth:unauthorized', handler);
  }, [authEnabled, authMethods, markNativeUnauthenticated, login]);

  // Handle 403 "Account is deactivated" — show clear error screen
  const [isDeactivated, setIsDeactivated] = useState(false);
  useEffect(() => {
    const handler = () => setIsDeactivated(true);
    window.addEventListener('auth:deactivated', handler);
    return () => window.removeEventListener('auth:deactivated', handler);
  }, []);

  // External form routes bypass Azure AD auth — token-authenticated public forms
  const isFormRoute = window.location.pathname.startsWith('/form/');
  if (isFormRoute) {
    return (
      <BrowserRouter>
        <ChunkErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/form/:token" element={<PublicFormPage />} />
            </Routes>
          </Suspense>
        </ChunkErrorBoundary>
      </BrowserRouter>
    );
  }

  // Client portal routes bypass Azure AD auth — they use cookie-based portal sessions.
  // Match /portal exactly or /portal/... but NOT /portal-queue or /staff-portal.
  // If the user is already authenticated through Azure AD, skip and let the staff routes
  // redirect /portal → /staff-portal. When Azure auth is disabled (local auth bypass / CI),
  // still allow the dedicated client portal routes to render.
  const isClientPortalRoute = /^\/portal(\/|$)/.test(window.location.pathname)
    && !window.location.pathname.startsWith('/portal-')
    && (!isAuthenticated || !authEnabled);
  if (isClientPortalRoute) {
    return (
      <BrowserRouter>
        <ChunkErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/portal" element={<PortalLayout />}>
                <Route index element={<PortalLogin />} />
                <Route path="verify" element={<PortalVerify />} />
                <Route path="dashboard" element={<PortalDashboard />} />
              </Route>
            </Routes>
          </Suspense>
        </ChunkErrorBoundary>
      </BrowserRouter>
    );
  }

  // Password-reset route bypasses auth — fully self-contained with its own API call.
  const isResetRoute = window.location.pathname === '/login/reset';
  if (isResetRoute) {
    return (
      <BrowserRouter>
        <ChunkErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login/reset" element={<ResetPasswordPage />} />
            </Routes>
          </Suspense>
        </ChunkErrorBoundary>
      </BrowserRouter>
    );
  }

  // Loading state while checking cached auth
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg, #fefcf9)',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}>
        <div style={{ textAlign: 'center', color: 'var(--text-muted, #a8a29e)' }}>
          <div style={{
            width: 36,
            height: 36,
            border: '3px solid var(--border, #f0e4dc)',
            borderTopColor: 'var(--primary, #f43f5e)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 1rem',
          }} />
          Loading...
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // Show deactivated screen when backend returns 403 "Account is deactivated"
  if (isDeactivated) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg, #fefcf9)',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}>
        <div style={{
          textAlign: 'center',
          maxWidth: 440,
          padding: '2.5rem',
          background: 'var(--card, #fff)',
          borderRadius: 12,
          border: '1px solid var(--border, #f0e4dc)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: '#fef2f2', display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 1.25rem',
            fontSize: '1.5rem',
          }}>
            !
          </div>
          <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.35rem', fontWeight: 700, color: 'var(--text, #1a1f36)' }}>
            Account Not Authorized
          </h1>
          <p style={{ margin: '0 0 1.5rem', fontSize: '0.92rem', color: 'var(--text-muted, #a8a29e)', lineHeight: 1.6 }}>
            Your account has been deactivated in Valentine. You signed in successfully
            with Microsoft, but your Valentine user account is currently turned off.
            Please contact your administrator to restore access.
          </p>
          <button
            onClick={() => { void logout(); }}
            style={{
              padding: '0.6rem 1.5rem',
              background: 'var(--primary, #f43f5e)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Sign Out
          </button>
          {logoutIssue && (
            <p role="alert" style={{ color: 'var(--danger, #dc2626)', marginTop: '0.75rem' }}>
              Sign out was not completed. Valentine is keeping this session visible so you can try again.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Show login page when auth is enabled but user isn't authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <ToastProvider>
    <ServiceWorkerUpdatePrompt />
    <BrowserRouter>
      <SwNavigationBridge />
      <LocationAwareErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Voice mode — full screen, outside AppLayout */}
          <Route path="/voice" element={<VoiceModePage />} />
          {/* Authenticated authority resolvers — full screen while minting a short-lived Maverick URL. */}
          <Route path="/maverick/case/:clusterId" element={<MaverickViewerRedirect kind="case" />} />
          <Route path="/maverick/statute/*" element={<MaverickViewerRedirect kind="statute" />} />
          {/* Staff routes inside AppLayout */}
          <Route element={<AppLayout />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/tasks/:id" element={<TasksPage />} />
            <Route path="/ai-hub" element={<AIHubPage />} />
            <Route path="/ai-hub/chat/:conversation_id" element={<InHubChatRoute />} />
            <Route path="/ai-hub/bringo-pilot-review" element={<AttorneyAdminRoute><BringoPilotReviewPage /></AttorneyAdminRoute>} />
            <Route path="/pipeline" element={<PipelineBoard />} />
            <Route path="/leads" element={<LeadsQueue />} />
            <Route path="/matters" element={<MattersList />} />
            <Route path="/matters/needs-review" element={<StaffRoute><MattersNeedingReviewPage /></StaffRoute>} />
            <Route path="/case-queue" element={<AttorneyQueuePage />} />
            <Route path="/contacts" element={<ContactsList />} />
            <Route path="/contacts/:id" element={<ContactDetail />} />
            <Route path="/matters/:id" element={<MatterDetail />} />
            <Route path="/matters/:matterId/workbench" element={<WorkbenchPage />} />
            <Route path="/matters/:matterId/scheduling-order-review/:reviewId" element={<SchedulingOrderReviewPage />} />
            <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />
            <Route path="/admin/feedback" element={<AdminRoute><AdminFeedback /></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
            <Route path="/admin/departure" element={<AdminRoute><AdminDeparture /></AdminRoute>} />
            <Route path="/admin/pipelines" element={<AdminRoute><AdminPipelines /></AdminRoute>} />
            <Route path="/admin/kpis/definitions" element={<AdminRoute><AdminKpis /></AdminRoute>} />
            <Route path="/admin/practice-areas" element={<AdminRoute><AdminPracticeAreas /></AdminRoute>} />
            <Route path="/admin/custom-fields" element={<AdminRoute><AdminCustomFields /></AdminRoute>} />
            <Route path="/admin/ringcentral" element={<AdminRoute><AdminRcSync /></AdminRoute>} />
            <Route path="/admin/dropbox" element={<AdminRoute><AdminDropbox /></AdminRoute>} />
            <Route path="/admin/email" element={<AdminRoute><AdminEmail /></AdminRoute>} />
            <Route path="/admin/protocols" element={<AdminRoute><AdminProtocols /></AdminRoute>} />
            <Route path="/admin/todoist" element={<AdminRoute><AdminTodoist /></AdminRoute>} />
            <Route path="/admin/forms" element={<AdminRoute><AdminForms /></AdminRoute>} />
            <Route path="/admin/signatures" element={<AdminSignatures />} />
            <Route path="/admin/signature-templates" element={<AdminSignatureTemplates />} />
            <Route path="/admin/file-router" element={<StaffRoute><AdminFileRouter /></StaffRoute>} />
            <Route path="/admin/tags" element={<AdminRoute><AdminTags /></AdminRoute>} />
            <Route path="/admin/webhooks" element={<AdminRoute><AdminWebhooks /></AdminRoute>} />
            <Route path="/admin/ai" element={<AdminRoute><AdminAICommandCenter /></AdminRoute>} />
            <Route path="/admin/ai/:tab" element={<AdminRoute><AdminAICommandCenter /></AdminRoute>} />
            <Route path="/admin/ai-cost" element={<AdminRoute><AdminAICostPage /></AdminRoute>} />
            <Route path="/admin/billing-export" element={<BillingClerkRoute><BillingExportPage /></BillingClerkRoute>} />
            <Route path="/admin/ai-billing" element={<AdminRoute><AdminAiBillingPage /></AdminRoute>} />
            <Route path="/my-billing" element={<BillingDetailPage />} />
            <Route path="/admin/rules-engine" element={<AdminRoute><AdminRulesEngine /></AdminRoute>} />
            <Route path="/admin/task-templates" element={<AdminRoute><AdminTaskTemplates /></AdminRoute>} />
            <Route path="/admin/retainers" element={<AdminRoute><AdminRetainers /></AdminRoute>} />
            <Route path="/admin/operations" element={<AdminRoute><AdminOperationsCompliance /></AdminRoute>} />
            <Route path="/admin/training-modules" element={<AdminRoute><AdminTrainingModules /></AdminRoute>} />
            <Route path="/admin/data-import" element={<AdminRoute><AdminDataImport /></AdminRoute>} />
            <Route path="/admin/data-import/clio-callback" element={<AdminRoute><DataImportClioCallback /></AdminRoute>} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/reports" element={<AdminRoute><ReportsPage /></AdminRoute>} />
            <Route path="/admin/kpis" element={<KpiSupervisorRoute><CommandCenter /></KpiSupervisorRoute>} />
            <Route path="/admin/kpis/call-case-bounties" element={<KpiSupervisorRoute><CallCaseBountiesPage /></KpiSupervisorRoute>} />
            <Route path="/admin/kpis/staff/:userId/intake-work" element={<KpiSupervisorRoute><KpiStaffIntakeWorkPage /></KpiSupervisorRoute>} />
            <Route path="/admin/kpis/staff/:userId" element={<KpiSupervisorRoute><KpiStaffDetailPage /></KpiSupervisorRoute>} />
            <Route path="/admin/kpis/alerts" element={<KpiSupervisorRoute><KpiAlertsPage /></KpiSupervisorRoute>} />
            <Route path="/admin/kpis/guide" element={<KpiSupervisorRoute><KpiGuidePage /></KpiSupervisorRoute>} />
            <Route path="/kpis/staff/:userId/intake-work" element={<LegacyKpiStaffRedirect intakeWork />} />
            <Route path="/kpis/staff/:userId" element={<LegacyKpiStaffRedirect />} />
            <Route path="/admin/drip-campaigns" element={<AdminRoute><AdminDripCampaigns /></AdminRoute>} />
            <Route path="/admin/phone-registry" element={<AdminRoute><PhoneRegistryAdmin /></AdminRoute>} />
            <Route path="/creditor-matches" element={<IntakeQueueRoute><CreditorMatchReview /></IntakeQueueRoute>} />
            <Route path="/admin/intake-ops" element={<AdminRoute><AdminIntakeOps /></AdminRoute>} />
            <Route path="/admin/intake-setup" element={<AdminRoute><AdminIntakeSetup /></AdminRoute>} />
            <Route path="/admin/mail-ops" element={<AdminRoute><AdminMailOps /></AdminRoute>} />
            <Route path="/admin/bulletin-board" element={<AdminRoute><AdminBulletinBoard /></AdminRoute>} />
            <Route path="/admin/billing" element={<AdminRoute><AdminBilling /></AdminRoute>} />
            <Route path="/admin/data-integrity" element={<AdminRoute><DataIntegrityReview /></AdminRoute>} />
            <Route path="/staff-portal" element={<StaffPortalPage />} />
            <Route path="/staff-portal/:tab" element={<StaffPortalPage />} />
            <Route path="/bulletin-board" element={<BulletinBoardPage />} />
            <Route path="/law-library" element={<StaffRoute><LawLibraryGate /></StaffRoute>} />
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/unmatched" element={<UnmatchedComms />} />
            <Route path="/communications/quarantine" element={<CommunicationsQuarantinePage />} />
            <Route path="/settings" element={<MySettings />} />
            <Route path="/settings/court-formats" element={<AttorneyAdminRoute><CourtFormatsPage /></AttorneyAdminRoute>} />
            <Route path="/guide" element={<UserGuide />} />
            <Route path="/guide/ai-verification" element={<AIVerificationHelpPage />} />
            <Route path="/employee-manual" element={<EmployeeManualPage />} />
            <Route path="/intake" element={<IntakeForm />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/billing/review" element={<BillingReviewRedirect />} />
            {APP_REDIRECT_ROUTES.map(({ path, to }) => (
              <Route key={path} path={path} element={<Navigate to={to} replace />} />
            ))}
          </Route>
        </Routes>
      </Suspense>
      </LocationAwareErrorBoundary>
      {DEMO_MODE && <DemoResetButton />}
      <InstallCoach onPermissionGranted={() => { void subscribeToPush(); }} />
    </BrowserRouter>
    </ToastProvider>
  );
}
